import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";

import {
  Visibility,
  Edit,
  Delete,
  PersonAddAltSharp,
} from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import PublicIcon from "@mui/icons-material/Public";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { green, red } from "@mui/material/colors";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import { formatDate } from "../../../utils/utils";
import { useAdminManagementContext } from "../../../context/AdminManagementContext";
import UserModelDialog from "./UserModelDialog";
import UserViewDialog from "./UserViewDialog";
import useStyles from "./AdminManagement.styles";
import { CREATE, UPDATE } from "../../../utils/constants";
import MuiConfirmDialog from "../../common/MuiConfirmDialog/MuiConfirmDialog";
import ExpandableSection from "../../common/ExpandableSection/ExpandableSection";
import SfcRegionModelDialog from "./SfcRegionModelDialog";
import UserAndRoleExport from "./UserAndRoleExport";
import { WA_ADMIN_MANAGEMENT } from "../../../pages";
import { useUserContext } from "../../../context/UserContext";

const userColumns = [
  { id: "email", label: "Email", minWidth: 10 },
  { id: "firstName", label: "First Name", minWidth: 10 },
  { id: "lastName", label: "Last Name", minWidth: 10 },
  { id: "lanId", label: "Lan Id", minWidth: 10 },
  { id: "requestType", label: "Request Type", minWidth: 10, maxWidth: 70 },
  {
    id: "active",
    label: "Is Active",
    minWidth: 5,
    align: "center",
    format: (value) => value,
  },
  {
    id: "admin",
    label: "Is Admin",
    minWidth: 5,
    align: "center",
    format: (value) => value,
  },
  {
    id: "applicationAdministrator",
    label: "Is Application Administrator",
    minWidth: 5,
    align: "center",
    format: (value) => value,
  },
  {
    id: "globalTechSupport",
    label: "Is Global Tech Support",
    minWidth: 5,
    align: "center",
    format: (value) => value,
  },
  {
    id: "userAccessProvisioner",
    label: "Is User Access Provisioner",
    minWidth: 5,
    align: "center",
    format: (value) => value,
  },
  { id: "createdBy", label: "Created By", minWidth: 10 },
  {
    id: "createdTime",
    label: "Created Time",
    maxWidth: 80,
    format: (value) => value.toLocaleString("en-US"),
  },
  { id: "updatedBy", label: "Updated By", minWidth: 10 },
  {
    id: "updatedTime",
    label: "Updated Time",
    maxWidth: 80,
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    id: "lastLoginDate",
    label: "Last Login Date",
    maxWidth: 80,
    format: (value) => value.toLocaleString("en-US"),
  },
];

const userTableColumns = [
  ...userColumns,
  {
    id: "actions",
    label: "Actions",
    minWidth: 10,
    align: "center",
    format: (value) => value,
  },
];

function createData({
  email,
  firstName,
  lastName,
  lanId,
  requestType,
  active,
  admin,
  applicationAdministrator,
  globalTechSupport,
  userAccessProvisioner,
  createdBy,
  createdTime,
  updatedBy,
  updatedTime,
  lastLoginDate,
  actions,
} = {}) {
  return {
    email,
    firstName,
    lastName,
    lanId,
    requestType,
    active,
    admin,
    applicationAdministrator,
    globalTechSupport,
    userAccessProvisioner,
    createdBy,
    createdTime: formatDate(createdTime),
    updatedBy,
    updatedTime: formatDate(updatedTime),
    lastLoginDate: formatDate(lastLoginDate),
    actions,
  };
}

const sfcRegionTableColumns = [
  { id: "regionName", label: "Region", minWidth: 100 },
  { id: "businessEntityID", label: "Business Entity ID", minWidth: 150 },
  { id: "localCurrency", label: "Local Currency", minWidth: 150 },
  { id: "country", label: "Country", minWidth: 150 },
  {
    id: "actions",
    label: "Actions",
    minWidth: 10,
    align: "center",
    format: (value) => value,
  },
];

function createSfcRowData({ sfc, region, index, actions } = {}) {
  return {
    regionName: region?.regionName, // Only show region name for the first SFC
    rowSpan: index === 0 ? region?.sfcs?.length : 0, // Only set rowSpan for the first SFC
    businessEntityID: sfc?.businessEntityID,
    localCurrency: sfc?.localCurrency,
    country: sfc?.country,
    sfcId: sfc?.sfcId,
    actions,
  };
}

const pageRoleAccessColumns = [
  { id: "pageRoleName", label: "Page Role Name", maxWidth: 70 },
  { id: "pageName", label: "Page Name", maxWidth: 140 },
  {
    id: "accessStatus",
    label: "Access Status",
    minWidth: 10,
    align: "center",
    format: (value) => value,
  },
];

function createPageRoleData({ pageRole, info, index, accessStatus } = {}) {
  return {
    pageRoleName: pageRole?.pageRoleName, // Only show Page Role Id for the first row
    rowSpan: index === 0 ? pageRole?.accessInfo?.length : 0, // Only set rowSpan for the first row
    pageName: info?.pageName,
    accessStatus,
    pageId: info?.pageId,
  };
}

const allUserAndRolesColumns = [
  ...userColumns,
  { id: "regionName", label: "Region Name", minWidth: 10 },
  { id: "businessEntityID", label: "Business Entity ID", minWidth: 10 },
  { id: "localCurrency", label: "Local Currency", minWidth: 10 },
  { id: "country", label: "Country", minWidth: 10 },
  { id: "writable", label: "Is Writable", minWidth: 10 },
];

function createAllUserAndRolesColumns({
  email,
  firstName,
  lastName,
  lanId,
  requestType,
  active,
  admin,
  applicationAdministrator,
  globalTechSupport,
  userAccessProvisioner,
  createdBy,
  createdTime,
  updatedBy,
  updatedTime,
  lastLoginDate,
  regionName,
  businessEntityID,
  localCurrency,
  country,
  writable,
} = {}) {
  return {
    email,
    firstName,
    lastName,
    lanId,
    requestType,
    active,
    admin,
    applicationAdministrator,
    globalTechSupport,
    userAccessProvisioner,
    createdBy,
    createdTime: formatDate(createdTime),
    updatedBy,
    updatedTime: formatDate(updatedTime),
    lastLoginDate: formatDate(lastLoginDate),
    regionName,
    businessEntityID,
    localCurrency,
    country,
    writable,
  };
}

const AdminManagement = ({ page } = {}) => {
  const { classes } = useStyles();
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [openViewUserModal, setOpenViewUserModal] = useState(false);

  const [openCreateSfcRegionModal, setOpenCreateSfcRegionModal] =
    useState(false);

  const {
    usersLoading,
    usersError,
    users,
    adminUsers,
    applicationAdministratorUsers,
    globalTechSupportUsers,
    userAccessProvisionerUsers,

    sfcRegionsLoading,
    sfcRegionsError,
    sfcRegions,

    usersWithRegionsAndSfcsLoading,
    usersWithRegionsAndSfcs,

    selectedUserDetailLoading,
    selectedUserDetail,
    setSelectedUserDetail,

    setSelectedUserEmail,

    setDeleteUserEmail,

    setUserFormData,

    setSfcRegionFormData,

    setDeleteSfcRegion,

    pageRoleAccess,
    updatePageRoleAccess,
    fetchAllPageRoleAccess,
  } = useAdminManagementContext();

  const { selectedUserDetail: loggedInUserDetail } = useUserContext() || {};

  const datas = users?.map(
    ({
      email,
      firstName,
      lastName,
      lanId,
      requestType,
      active,
      admin,
      applicationAdministrator,
      globalTechSupport,
      userAccessProvisioner,
      createdBy,
      createdTime,
      updatedBy,
      updatedTime,
      lastLoginDate,
    }) =>
      createData({
        email: (
          <div>
            {admin && (
              <StarIcon
                sx={{
                  fontSize: "22px",
                  marginRight: "5px",
                  marginBottom: "-6px",
                  color: "#ffd600",
                }}
              />
            )}
            {email}
          </div>
        ),
        firstName,
        lastName,
        lanId: lanId ?? "N/A",
        requestType,
        active: active ? (
          <CheckIcon sx={{ color: green[500] }} />
        ) : (
          <CloseIcon sx={{ color: red[400] }} />
        ),
        admin: admin ? (
          <CheckIcon sx={{ color: green[500] }} />
        ) : (
          <CloseIcon sx={{ color: red[400] }} />
        ),
        applicationAdministrator: applicationAdministrator ? (
          <CheckIcon sx={{ color: green[500] }} />
        ) : (
          <CloseIcon sx={{ color: red[400] }} />
        ),
        globalTechSupport: globalTechSupport ? (
          <CheckIcon sx={{ color: green[500] }} />
        ) : (
          <CloseIcon sx={{ color: red[400] }} />
        ),
        userAccessProvisioner: userAccessProvisioner ? (
          <CheckIcon sx={{ color: green[500] }} />
        ) : (
          <CloseIcon sx={{ color: red[400] }} />
        ),
        createdBy: createdBy ?? "N/A",
        createdTime: createdTime,
        updatedBy: updatedBy ?? "N/A",
        updatedTime: updatedTime,
        lastLoginDate: lastLoginDate,
        actions: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-evenly",
            }}
          >
            <IconButton
              style={{ color: "#7b7c7c" }}
              onClick={() => handleViewUser({ email })}
            >
              <Visibility />
            </IconButton>
            {loggedInUserDetail?.userAccessProvisioner && (
              <IconButton
                style={{ color: "#6ba9de" }}
                onClick={() => handleEditUser({ email })}
              >
                <Edit />
              </IconButton>
            )}
            {loggedInUserDetail?.userAccessProvisioner && (
              <IconButton
                style={{ color: "#ea6e6c" }}
                onClick={() => handleDeleteUser({ email, firstName, lastName })}
              >
                <Delete />
              </IconButton>
            )}
          </div>
        ),
      }),
  );

  const handleCreateUser = () => {
    setOpenCreateUserModal(true);
  };

  const handleEditUser = ({ email }) => {
    setSelectedUserEmail(email);
    setOpenEditUserModal(true);
  };

  const handleViewUser = ({ email }) => {
    setSelectedUserEmail(email);
    setOpenViewUserModal(true);
  };

  const flattenSfcRegions = (regions) => {
    return (
      regions?.flatMap(
        (region) =>
          // For each region, map its sfcs into row data using the helper function
          region?.sfcs?.map((sfc, index) =>
            createSfcRowData({
              sfc,
              region,
              index,
              actions: (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-evenly",
                  }}
                >
                  {region?.regionName.toLowerCase() === "global" ? (
                    <Typography variant="body1">(Not Allowed)</Typography>
                  ) : (
                    <IconButton
                      style={{ color: "#ea6e6c" }}
                      onClick={() =>
                        handleDeleteSfcRegion({
                          regionName: region?.regionName,
                          businessEntityID: sfc?.businessEntityID,
                        })
                      }
                      disabled={region?.regionName.toLowerCase() === "global"}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </div>
              ),
            }),
          ) || [], // If sfcs is null/undefined, return an empty array for flatMap
      ) || []
    ); // If regions is null/undefined, return an empty array
  };
  const sfcRegionsDatas = flattenSfcRegions(sfcRegions);

  // MUI confirm Delete Dialog
  const [showConfirmDialogUser, setShowConfirmDialogUser] = useState(false);
  const [itemToDeleteUser, setItemToDeleteUser] = useState(null);

  const handleDeleteUser = ({ email, firstName, lastName }) => {
    setItemToDeleteUser({ email, firstName, lastName });
    setShowConfirmDialogUser(true);
  };

  const handleConfirmDeleteUser = () => {
    setDeleteUserEmail(itemToDeleteUser?.email);
    setShowConfirmDialogUser(false);
    setItemToDeleteUser(null);
  };

  const handleCancelDeleteUser = () => {
    setShowConfirmDialogUser(false);
    setItemToDeleteUser(null);
  };

  const handleCreateSfcRegion = () => {
    setOpenCreateSfcRegionModal(true);
  };

  const [showConfirmDialogSfcRegion, setShowConfirmDialogSfcRegion] =
    useState(false);
  const [itemToDeleteSfcRegion, setItemToDeleteSfcRegion] = useState(null);

  const handleDeleteSfcRegion = ({ regionName, businessEntityID }) => {
    setItemToDeleteSfcRegion({ regionName, businessEntityID });
    setShowConfirmDialogSfcRegion(true);
  };

  const handleConfirmDeleteSfcRegion = () => {
    setDeleteSfcRegion({
      regionName: itemToDeleteSfcRegion?.regionName,
      businessEntityID: itemToDeleteSfcRegion?.businessEntityID,
    });
    setShowConfirmDialogSfcRegion(false);
    setItemToDeleteSfcRegion(null);
  };

  const handleCancelDeleteSfcRegion = () => {
    setShowConfirmDialogSfcRegion(false);
    setItemToDeleteSfcRegion(null);
  };

  const datasForUserRegionsAndSfcs = usersWithRegionsAndSfcs?.flatMap(
    (user) => {
      const {
        email,
        firstName,
        lastName,
        lanId,
        requestType,
        active,
        admin,
        applicationAdministrator,
        globalTechSupport,
        userAccessProvisioner,
        createdBy,
        createdTime,
        updatedBy,
        updatedTime,
        lastLoginDate,
        regions,
      } = user || {};

      if (!regions || regions?.length === 0) {
        return [];
      }

      return regions?.flatMap((region) => {
        const { regionName, sfcs } = region || {};

        if (!sfcs || sfcs.length === 0) {
          return [
            createAllUserAndRolesColumns({
              email,
              firstName,
              lastName,
              lanId: lanId ?? "N/A",
              requestType,
              active,
              admin,
              applicationAdministrator,
              globalTechSupport,
              userAccessProvisioner,
              createdBy: createdBy ?? "N/A",
              createdTime,
              updatedBy: updatedBy ?? "N/A",
              updatedTime,
              lastLoginDate,
              regionName,
              businessEntityID: null, // No SFC, so null
              localCurrency: null, // No SFC, so null
              country: null, // No SFC, so null
              writable: null, // No SFC, so null
            }),
          ];
        }

        return sfcs?.map((sfc) => {
          const { businessEntityID, localCurrency, country, writable } =
            sfc || {};

          return createAllUserAndRolesColumns({
            email,
            firstName,
            lastName,
            lanId: lanId ?? "N/A",
            requestType,
            active,
            admin,
            applicationAdministrator,
            globalTechSupport,
            userAccessProvisioner,
            createdBy: createdBy ?? "N/A",
            createdTime,
            updatedBy: updatedBy ?? "N/A",
            updatedTime,
            lastLoginDate,
            regionName,
            businessEntityID,
            localCurrency,
            country,
            writable,
          });
        });
      });
    },
  );

  const datasForAdminUsers = adminUsers?.flatMap((user) => {
    const {
      email,
      firstName,
      lastName,
      lanId,
      requestType,
      active,
      admin,
      applicationAdministrator,
      globalTechSupport,
      userAccessProvisioner,
      createdBy,
      createdTime,
      updatedBy,
      updatedTime,
      lastLoginDate,
    } = user || {};

    return [
      createAllUserAndRolesColumns({
        email,
        firstName,
        lastName,
        lanId: lanId ?? "N/A",
        requestType,
        active,
        admin,
        applicationAdministrator,
        globalTechSupport,
        userAccessProvisioner,
        createdBy: createdBy ?? "N/A",
        createdTime,
        updatedBy: updatedBy ?? "N/A",
        updatedTime,
        lastLoginDate,
      }),
    ];
  });

  const datasForApplicationAdministratorUsers =
    applicationAdministratorUsers?.flatMap((user) => {
      const {
        email,
        firstName,
        lastName,
        lanId,
        requestType,
        active,
        admin,
        applicationAdministrator,
        globalTechSupport,
        userAccessProvisioner,
        createdBy,
        createdTime,
        updatedBy,
        updatedTime,
        lastLoginDate,
      } = user || {};

      return [
        createAllUserAndRolesColumns({
          email,
          firstName,
          lastName,
          lanId: lanId ?? "N/A",
          requestType,
          active,
          admin,
          applicationAdministrator,
          globalTechSupport,
          userAccessProvisioner,
          createdBy: createdBy ?? "N/A",
          createdTime,
          updatedBy: updatedBy ?? "N/A",
          updatedTime,
          lastLoginDate,
        }),
      ];
    });

  const datasForUserAccessProvisionerUsers =
    userAccessProvisionerUsers?.flatMap((user) => {
      const {
        email,
        firstName,
        lastName,
        lanId,
        requestType,
        active,
        admin,
        applicationAdministrator,
        globalTechSupport,
        userAccessProvisioner,
        createdBy,
        createdTime,
        updatedBy,
        updatedTime,
        lastLoginDate,
      } = user || {};

      return [
        createAllUserAndRolesColumns({
          email,
          firstName,
          lastName,
          lanId: lanId ?? "N/A",
          requestType,
          active,
          admin,
          applicationAdministrator,
          globalTechSupport,
          userAccessProvisioner,
          createdBy: createdBy ?? "N/A",
          createdTime,
          updatedBy: updatedBy ?? "N/A",
          updatedTime,
          lastLoginDate,
        }),
      ];
    });

  const datasForGlobalTechSupportUsers = globalTechSupportUsers?.flatMap(
    (user) => {
      const {
        email,
        firstName,
        lastName,
        lanId,
        requestType,
        active,
        admin,
        applicationAdministrator,
        globalTechSupport,
        userAccessProvisioner,
        createdBy,
        createdTime,
        updatedBy,
        updatedTime,
        lastLoginDate,
      } = user || {};

      return [
        createAllUserAndRolesColumns({
          email,
          firstName,
          lastName,
          lanId: lanId ?? "N/A",
          requestType,
          active,
          admin,
          applicationAdministrator,
          globalTechSupport,
          userAccessProvisioner,
          createdBy: createdBy ?? "N/A",
          createdTime,
          updatedBy: updatedBy ?? "N/A",
          updatedTime,
          lastLoginDate,
        }),
      ];
    },
  );

  const [selectedPageRoleId, setSelectedPageRoleId] = useState(null);
  const [filteredPageAccessInfo, setFilteredPageAccessInfo] = useState(null);

  useEffect(() => {
    if (selectedPageRoleId) {
      const foundPageRoleId = pageRoleAccess?.find(
        (role) => role.pageRoleId === selectedPageRoleId,
      );
      if (foundPageRoleId) {
        setFilteredPageAccessInfo(foundPageRoleId);
      } else {
        setFilteredPageAccessInfo(null);
      }
    } else {
      setFilteredPageAccessInfo(null);
    }
  }, [selectedPageRoleId, pageRoleAccess]);

  const handlePageRoleIdChange = (event, newValue) => {
    if (newValue) {
      setSelectedPageRoleId(newValue?.pageRoleId);
    } else {
      setSelectedPageRoleId(null);
    }
    fetchAllPageRoleAccess();
  };

  const handleAccessChange = (index) => {
    const originalAccessValue =
      filteredPageAccessInfo?.accessInfo?.[index]?.access;

    const updatedPageRole = {
      ...filteredPageAccessInfo,
      accessInfo: filteredPageAccessInfo?.accessInfo?.map((item, i) =>
        i === index ? { ...item, access: !item.access } : item,
      ),
    };
    setFilteredPageAccessInfo(updatedPageRole);

    updatePageRoleAccess({
      pageRoleId: updatedPageRole?.pageRoleId,
      pageRoleName: updatedPageRole?.pageRoleName,
      pageId: updatedPageRole?.accessInfo?.[index]?.pageId,
      access: updatedPageRole?.accessInfo?.[index]?.access,
    })
      .then((status) => {
        if (!status) {
          setFilteredPageAccessInfo((prev) => {
            const newAccessInfo = prev?.accessInfo?.map((item, i) =>
              i === index ? { ...item, access: originalAccessValue } : item,
            );
            return { ...prev, accessInfo: newAccessInfo };
          });
          alert("Failed to update access status!");
        }
      })
      .catch((error) => {
        setFilteredPageAccessInfo((prev) => {
          const newAccessInfo = prev?.accessInfo?.map((item, i) =>
            i === index ? { ...item, access: originalAccessValue } : item,
          );
          return { ...prev, accessInfo: newAccessInfo };
        });
        alert(error);
      })
      .finally(() => {});
  };

  const flattenPageRoleAccess = () => {
    return (
      filteredPageAccessInfo?.accessInfo?.map((info, index) =>
        createPageRoleData({
          pageRole: filteredPageAccessInfo,
          info,
          index,
          accessStatus: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
              }}
            >
              {info?.pageId === WA_ADMIN_MANAGEMENT ? (
                <Typography sx={{ mt: 2, mb: 2, fontSize: "0.8rem" }}>
                  CONTACT SUPPORT
                </Typography>
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={info?.access || false}
                      onChange={() => handleAccessChange(index)}
                      color="success"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#4caf50",
                            opacity: 1,
                          },
                        "& .MuiSwitch-switchBase": {
                          "&:not(.Mui-checked) + .MuiSwitch-track": {
                            backgroundColor: "#f44336",
                            opacity: 1,
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#bdbdbd",
                          opacity: 1,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ml: 1, fontSize: "0.8rem" }}>
                      {info?.access ? "(Enabled)" : "(Disabled)"}
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </div>
          ),
        }),
      ) || []
    );
  };
  const pageRoleAccessDatas = flattenPageRoleAccess();

  return (
    <div className={classes.page}>
      <Grid container item spacing={2}>
        {/* Show Manage Users and Roles section only for User Access Provisioner role */}

        <ExpandableSection title="Manage Users and Roles">
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Grid item>
              <Typography sx={{ ml: 1 }}>Showing List of Users</Typography>
            </Grid>
            <Grid item sx={{ display: "flex", gap: 1 }}>
              <Typography
                sx={{
                  ml: 1,
                  fontSize: "15px",
                  marginRight: "10px",
                  marginTop: "10px",
                }}
              >
                {"("}
                <StarIcon
                  sx={{
                    fontSize: "15px",
                    marginBottom: "-2px",
                    color: "#ffd600",
                  }}
                />
                - Denotes Admin Users{")"}
              </Typography>
              <UserAndRoleExport
                columns={allUserAndRolesColumns}
                datas={datasForUserRegionsAndSfcs}
                columnsForAdmins={userColumns}
                datasForAdmins={datasForAdminUsers}
                columnsForApplicationAdministrator={userColumns}
                datasForApplicationAdministrator={
                  datasForApplicationAdministratorUsers
                }
                columnsForGlobalTechSupport={userColumns}
                datasForGlobalTechSupport={datasForGlobalTechSupportUsers}
                columnsForUserAccessProvisioner={userColumns}
                datasForUserAccessProvisioner={
                  datasForUserAccessProvisionerUsers
                }
                disabled={usersWithRegionsAndSfcsLoading}
              />
              {loggedInUserDetail?.userAccessProvisioner && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateUser}
                  sx={{ mr: 1 }}
                >
                  <PersonAddAltSharp sx={{ mr: 1 }} />
                  Add User
                </Button>
              )}
            </Grid>
          </Grid>

          {!usersLoading && usersError ? (
            <Typography color="error">
              There was an error loading users.
            </Typography>
          ) : !usersLoading && (!users || users.length === 0) ? (
            <Typography>No Users Found!</Typography>
          ) : (
            <ColumnGroupingTable
              columns={userTableColumns}
              datas={datas}
              loading={usersLoading}
            />
          )}
        </ExpandableSection>
        {/* Show Manage Regions and SFC's section and Manage Page by Roles only for Full Admin Access role */}
        {loggedInUserDetail?.admin && (
          <>
            <ExpandableSection title="Manage Regions and SFC's">
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography sx={{ ml: 1 }}>
                  Showing List of Regions and SFC's
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateSfcRegion}
                  sx={{ mr: 1 }}
                >
                  <PublicIcon sx={{ mr: 1 }} />
                  Add Region / SFC
                </Button>
              </Grid>

              {!sfcRegionsLoading && sfcRegionsError ? (
                <Typography color="error">
                  There was an error loading Regions / SFC's.
                </Typography>
              ) : !sfcRegionsLoading &&
                (!sfcRegions || sfcRegions.length === 0) ? (
                <Typography>No Regions / SFC's Found!</Typography>
              ) : (
                <ColumnGroupingTable
                  columns={sfcRegionTableColumns}
                  datas={sfcRegionsDatas}
                  loading={sfcRegionsLoading}
                  disablePagination={true}
                  rowSpanKey="regionName" // To merge and center vertically
                />
              )}
            </ExpandableSection>

            <ExpandableSection title="Manage Page by Roles">
              <Typography sx={{ ml: 1, mb: 2 }}>
                Showing List of Pages by Roles
              </Typography>

              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    options={pageRoleAccess}
                    sx={{ width: 300 }}
                    onChange={handlePageRoleIdChange}
                    getOptionLabel={(option) => option?.pageRoleName}
                    renderInput={(params) => (
                      <TextField {...params} label="Page Role Name" />
                    )}
                  />
                </Grid>
              </Grid>
              {!selectedPageRoleId ? (
                <Typography color="error">
                  Please select a Page Role Id to view the access information.
                </Typography>
              ) : !filteredPageAccessInfo ? (
                <Typography>No Pages found!</Typography>
              ) : (
                <ColumnGroupingTable
                  columns={pageRoleAccessColumns}
                  datas={pageRoleAccessDatas}
                  loading={false}
                  disablePagination={true}
                  rowSpanKey="pageRoleName" // To merge and center vertically
                />
              )}
            </ExpandableSection>
          </>
        )}
      </Grid>

      {!selectedUserDetailLoading && (
        <>
          {!sfcRegionsLoading && (
            <UserModelDialog
              users={users}
              sfcRegions={sfcRegions}
              openModal={openEditUserModal}
              setOpenModal={setOpenEditUserModal}
              setSelectedUserEmail={setSelectedUserEmail}
              selectedUserDetail={selectedUserDetail}
              setSelectedUserDetail={setSelectedUserDetail}
              setUserFormData={setUserFormData}
              mode={UPDATE}
            />
          )}

          <UserViewDialog
            openModal={openViewUserModal}
            setOpenModal={setOpenViewUserModal}
            setSelectedUserEmail={setSelectedUserEmail}
            selectedUserDetail={selectedUserDetail}
            setSelectedUserDetail={setSelectedUserDetail}
          />
        </>
      )}

      {!sfcRegionsLoading && (
        <UserModelDialog
          users={users}
          sfcRegions={sfcRegions}
          openModal={openCreateUserModal}
          setOpenModal={setOpenCreateUserModal}
          setSelectedUserEmail={setSelectedUserEmail}
          setUserFormData={setUserFormData}
          mode={CREATE}
        />
      )}

      <MuiConfirmDialog
        open={showConfirmDialogUser}
        title="Confirm Deletion"
        message={`Are you sure you want to permanently delete the user '${itemToDeleteUser?.email}' (${itemToDeleteUser?.firstName} ${itemToDeleteUser?.lastName})? This action cannot be undone.`}
        onConfirm={handleConfirmDeleteUser}
        onCancel={handleCancelDeleteUser}
      />

      {!sfcRegionsLoading && (
        <SfcRegionModelDialog
          sfcRegions={sfcRegions}
          openModal={openCreateSfcRegionModal}
          setOpenModal={setOpenCreateSfcRegionModal}
          setSfcRegionFormData={setSfcRegionFormData}
        />
      )}

      <MuiConfirmDialog
        open={showConfirmDialogSfcRegion}
        title="Confirm Deletion"
        message={`Please note: All the users who has access to this Region/Sfc will be Revoked. Are you sure you want to permanently delete the Sfc: '${itemToDeleteSfcRegion?.businessEntityID}' under the Region: '${itemToDeleteSfcRegion?.regionName}'? This action cannot be undone.`}
        onConfirm={handleConfirmDeleteSfcRegion}
        onCancel={handleCancelDeleteSfcRegion}
      />
    </div>
  );
};

export default AdminManagement;
